import { uniqBy } from './object-utils';
import * as cmxDateTime from './date-time/cmx-date-time';

enum CodeTypes {
  ICD = 'ICD',
  CPT = 'CPT',
  TECH = 'TECH',
  MODIFIER = 'MODIFIER'
}

const processModifiers = (
  modifiers: CMxAPI.ModifierSection,
  caseRun: CMxAPI.CaseRun,
  codeRequests: CMxAPI.CodeRequest[]
) => {
  modifiers.codes.forEach(mod => {
    codeRequests.push({
      code: mod.modifier_code,
      codeType: CodeTypes.MODIFIER,
      effectiveDate:
        caseRun.date_of_service ||
        cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
      dictionaryName: 'Modifiers',
      columnName: 'Code'
    });
  });
};

const processICDs = (
  icds: CMxAPI.DiagnosticSection,
  caseRun: CMxAPI.CaseRun,
  codeRequests: CMxAPI.CodeRequest[]
) => {
  icds.codes.forEach(icd => {
    codeRequests.push({
      code: icd.diagnosis_code,
      codeType: CodeTypes.ICD,
      effectiveDate:
        caseRun.date_of_service ||
        cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
      dictionaryName: 'ICD Codes',
      columnName: 'Code'
    });
  });
};

const processCPTs = (
  procedures: CMxAPI.ProcedureSection,
  caseRun: CMxAPI.CaseRun,
  codeRequests: CMxAPI.CodeRequest[]
) => {
  procedures.codes.forEach(procedure => {
    codeRequests.push({
      code: procedure.procedure_code,
      codeType: CodeTypes.CPT,
      effectiveDate:
        caseRun.date_of_service ||
        cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
      dictionaryName: 'CPT Codes',
      columnName: 'Code'
    });
    if (
      procedure.modifiers &&
      procedure.modifiers.codes &&
      Array.isArray(procedure.modifiers.codes) &&
      procedure.modifiers.codes.length > 0
    ) {
      processModifiers(procedure.modifiers, caseRun, codeRequests);
    }
    if (
      procedure.diagnoses &&
      procedure.diagnoses.codes &&
      Array.isArray(procedure.diagnoses.codes) &&
      procedure.diagnoses.codes.length > 0
    ) {
      processICDs(procedure.diagnoses, caseRun, codeRequests);
    }
  });
};

export const getCodeRequests = (
  artifacts: CMxAPI.CaseArtifacts[],
  caseRun: CMxAPI.CaseRun
) => {
  const codeRequests: CMxAPI.CodeRequest[] = [];
  artifacts.forEach(artifact => {
    processCPTs(artifact.procedures, caseRun, codeRequests);

    if (artifact.modifiers) {
      processModifiers(artifact.modifiers, caseRun, codeRequests);
    }
    if (artifact.diagnoses) {
      processICDs(artifact.diagnoses, caseRun, codeRequests);
    }
  });
  return uniqBy(
    codeRequests,
    (code: CMxAPI.CodeRequest) => `${code.code}-${code.effectiveDate}`
  );
};

export const getInvoiceCodeRequests = (
  caseRuns: CMxAPI.CaseRunV2[] | CMxAPI.CaseRun[]
) => {
  const codeRequests: CMxAPI.CodeRequest[] = [];
  caseRuns.forEach(caseRun => {
    (caseRun.invoices || []).forEach(invoice => {
      (invoice.transactions || []).forEach(txn => {
        if (txn.procedure) {
          codeRequests.push({
            code: `${txn.procedure.procedure_code}`,
            codeType: CodeTypes.CPT,
            effectiveDate:
              caseRun.date_of_service ||
              cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
            dictionaryName: 'CPT Codes',
            columnName: 'Code'
          });

          // Add modifiers for procedure level
          if (txn.procedure.modifier_codes) {
            txn.procedure.modifier_codes.forEach(mod => {
              codeRequests.push({
                code: mod.modifier_code,
                codeType: CodeTypes.MODIFIER,
                effectiveDate:
                  caseRun.date_of_service ||
                  cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
                dictionaryName: 'Modifiers',
                columnName: 'Code'
              });
            });
          }
        }

        if (txn.technical_code) {
          codeRequests.push({
            code: `${txn.technical_code.code}`,
            codeType: CodeTypes.TECH,
            effectiveDate:
              caseRun.date_of_service ||
              cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
            dictionaryName: 'Technical Charge Codes',
            columnName: 'Code'
          });

          // Add modifiers for technical_code level
          if (txn.technical_code.modifier_codes) {
            txn.technical_code.modifier_codes.forEach(mod => {
              codeRequests.push({
                code: mod.modifier_code,
                codeType: CodeTypes.MODIFIER,
                effectiveDate:
                  caseRun.date_of_service ||
                  cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
                dictionaryName: 'Modifiers',
                columnName: 'Code'
              });
            });
          }
        }

        if (txn.diagnoses) {
          txn.diagnoses.forEach(dx => {
            codeRequests.push({
              code: dx.diagnosis_code,
              codeType: CodeTypes.ICD,
              effectiveDate:
                caseRun.date_of_service ||
                cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
              dictionaryName: 'ICD Codes',
              columnName: 'Code'
            });
          });
        }
      });
    });
  });
  return uniqBy(
    codeRequests,
    (code: CMxAPI.CodeRequest) => `${code.code}-${code.effectiveDate}`
  );
};
